import dayjs from 'dayjs'
import { createReducer } from 'redux-create-reducer'
import { statsEndpoints } from '@disco/api/server'
import {
  STATS_CHANGE_MODE,
  STATS_CHART_LOAD,
  STATS_CHART_LOAD_FAIL,
  STATS_CHART_LOAD_SUCCESS,
  STATS_LOAD,
  STATS_LOAD_FAIL,
  STATS_LOAD_SUCCESS,
  STATS_PARAMS_RESET,
  STATS_PARAMS_UPDATE,
  STATS_SUMMARY_LOAD,
  STATS_SUMMARY_LOAD_FAIL,
  STATS_SUMMARY_LOAD_SUCCESS,
} from '@disco/constants/ActionTypes'
type StatEntity = {
  loading: boolean
  error: Error | null | undefined
  data: any[]
  next: string | null | undefined
  timestamp: Date | null | undefined
}
type StatEntities = {
  playlists?: {
    views: StatEntity
    downloads: StatEntity
  }
  tracks?: {
    playbacks: StatEntity
    pitches: StatEntity
    downloads: StatEntity
  }
  users?: {
    playlists: StatEntity
    activity: StatEntity
  }
}

const getEntities = (): StatEntities =>
  Object.keys(statsEndpoints).reduce(
    (acc, name: string) => ({
      [name]: Object.keys(statsEndpoints[name]).reduce(
        (data, kind) => ({
          [kind]: {
            loading: false,
            error: null,
            data: [],
            next: null,
            timestamp: null,
          },
          ...data,
        }),
        {},
      ),
      ...acc,
    }),
    {},
  )

export type State = {
  mode: string | null | undefined
  startDate: Date
  endDate: Date
  summary: {
    loading: boolean
    error: Error | null | undefined
    data: Record<string, any> | null | undefined
  }
  charts: {
    tracks: {
      loading: boolean
      error: Error | null | undefined
      data: Record<string, any>[]
    }
    playlists: {
      loading: boolean
      error: Error | null | undefined
      data: Record<string, any>[]
    }
  }
  entities: StatEntities
}
const initialState: State = {
  // 'end-user' or 'team',
  // depending on currently selected view
  mode: null,
  startDate: dayjs().subtract(30, 'd').toDate(),
  endDate: dayjs().toDate(),
  summary: {
    loading: false,
    error: null,
    data: {},
  },
  charts: {
    tracks: {
      loading: false,
      error: null,
      data: [],
    },
    playlists: {
      loading: false,
      error: null,
      data: [],
    },
  },
  entities: getEntities(),
}

function updateEntity(
  state: State,
  key: string,
  kind: string,
  update: Record<string, any>,
): State {
  return {
    ...state,
    entities: {
      ...state.entities,
      [key]: {
        ...state.entities[key],
        [kind]: { ...state.entities[key][kind], ...update },
      },
    },
  }
}

export const statsReducer = createReducer<State, any>(initialState, {
  [STATS_CHANGE_MODE]: (state, { mode }) => ({ ...state, mode }),
  [STATS_PARAMS_RESET]: () => initialState,
  [STATS_PARAMS_UPDATE]: (state, { fields }) => ({ ...state, ...fields }),
  [STATS_SUMMARY_LOAD]: ({ summary, ...state }) => ({
    ...state,
    summary: { ...summary, loading: true },
  }),
  [STATS_SUMMARY_LOAD_SUCCESS]: ({ summary, ...state }, { data }) => ({
    ...state,
    summary: {
      loading: false,
      error: null,
      data,
    },
  }),
  [STATS_SUMMARY_LOAD_FAIL]: ({ summary, ...state }, { error }) => ({
    ...state,
    summary: {
      loading: false,
      error,
      data: null,
    },
  }),
  [STATS_CHART_LOAD]: ({ charts, ...state }, { key }) => ({
    ...state,
    charts: { ...charts, [key]: { ...charts[key], loading: true } },
  }),
  [STATS_CHART_LOAD_SUCCESS]: ({ charts, ...state }, { key, data }) => ({
    ...state,
    charts: {
      ...charts,
      [key]: { ...charts[key], loading: false, error: null, data },
    },
  }),
  [STATS_CHART_LOAD_FAIL]: ({ charts, ...state }, { key, error }) => ({
    ...state,
    charts: { ...charts, [key]: { ...charts[key], loading: false, error } },
  }),
  [STATS_LOAD]: (state, { key, kind, next, timestamp }) => {
    const update = {
      loading: true,
      next,
      timestamp,
    }
    return updateEntity(state, key, kind, update)
  },
  [STATS_LOAD_SUCCESS]: (state, { key, kind, data, next, timestamp }) => {
    const update = {
      loading: false,
      error: null,
      data,
      next,
    }
    return timestamp === state.entities[key][kind].timestamp
      ? updateEntity(state, key, kind, update)
      : state
  },
  [STATS_LOAD_FAIL]: (state, { key, kind, error, timestamp }) => {
    const update = {
      loading: false,
      error,
    }
    return timestamp === state.entities[key][kind].timestamp
      ? updateEntity(state, key, kind, update)
      : state
  },
})
